import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core'
import { trigger, state, style, transition, animate } from '@angular/animations'
import { getResourceNameFromArn, getResourceTypeDisplayString } from 'src/app/shared/utils'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ContentSubGroup, ViewableIssue } from 'src/app/account/components/issues-view/issues-view.component'
import { Application } from '../../../services/application.service'
import { consoleUrl } from '../../../../../utils/aws-utils'
import { DateFormattingService } from 'src/app/services/date-formatting.service'
import { ClipboardService } from 'src/app/services/clipboard.service'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'

const accordionSlide = trigger('accordionSlide', [
  state('collapsed', style({ height: '0px', paddingTop: '0', paddingBottom: '0' })),
  state('expanded', style({ height: '*', paddingTop: '*', paddingBottom: '*' })),
  transition('collapsed <=> expanded', animate('200ms ease-in-out')),
])

// Segments of a resource name split into common prefix/unique middle/common suffix for diff highlighting
export interface ResourceSegments {
  prefix: string
  unique: string
  suffix: string
  full: string
  hasDiff: boolean // true when prefix or suffix was found, enabling visual emphasis on the unique part
}

export interface ResourceEntry {
  issue: ViewableIssue
  segments: ResourceSegments
  consoleUrl: string
  typeLabel: string
}

export interface ResourceTypeGroup {
  label: string
  entries: ResourceEntry[]
}

/**
 * Compute longest common prefix of an array of strings.
 */
function commonPrefix(strings: string[]): string {
  if (strings.length === 0) return ''
  let prefix = strings[0]
  for (let i = 1; i < strings.length; i++) {
    while (strings[i].indexOf(prefix) !== 0) {
      prefix = prefix.substring(0, prefix.length - 1)
      if (prefix === '') return ''
    }
  }
  return prefix
}

/**
 * Compute longest common suffix of an array of strings.
 */
function commonSuffix(strings: string[]): string {
  const reversed = strings.map((s) => s.split('').reverse().join(''))
  const cp = commonPrefix(reversed)
  return cp.split('').reverse().join('')
}

// tslint:disable:component-selector
@Component({
  selector: 'issue-content-group',
  templateUrl: './issue-content-group.component.html',
  styleUrls: ['./issue-content-group.component.scss'],
  animations: [accordionSlide],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [CopyFeedbackService],
})
export class IssueContentGroupComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() subGroup: ContentSubGroup = null
  @Input('sync-pair') syncPair: SyncPair = null
  @Input('application') application: Application = null
  @Input() flat = false // flat mode: no accordion header, panel always visible (used for single-issue groups)
  @Input() @HostBinding('class.grouped') grouped = false // true when nested inside a parent type-level accordion
  @Output() resourceClicked = new EventEmitter<string>()

  // Header chip expand-on-click state — the single-resource chip in the accordion
  // header truncates long names at 300px. Click toggles full wrapped display,
  // mirroring the resource-table `.cell-truncatable` pattern.
  headerChipExpanded = false

  // Both labels build and join a distinct-type set, so they're stamped on input change
  // rather than recomputed on every change-detection pass.
  accordionLabel = ''
  resourceTypeLabel = ''

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private dateFormat: DateFormattingService,
    private clipboard: ClipboardService,
    public copyFeedback: CopyFeedbackService
  ) {}

  ngAfterViewInit(): void {
    // Event-delegated mouseover — stamps .is-truncated on .inline-copy-text spans
    // only when scrollWidth > clientWidth. Runs outside the Angular zone to avoid
    // change detection on every mouse move.
    this.zone.runOutsideAngular(() => {
      this.el.nativeElement.addEventListener('mouseover', this.onTruncatableMouseOver)
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.subGroup || changes.grouped) {
      this.accordionLabel = this.computeAccordionLabel()
      this.resourceTypeLabel = this.computeResourceTypeLabel()
    }

    // Only reset the header chip's expanded state when the chip's resource
    // actually changes — polling replaces the subGroup reference each cycle but
    // usually points at the same resource, and re-collapsing on every poll
    // would defeat the user's click to expand a long ARN.
    if (changes.subGroup) {
      const prev = changes.subGroup.previousValue?.issues?.[0]
      const curr = changes.subGroup.currentValue?.issues?.[0]
      const prevName = prev ? this.resourceNameString(prev) : null
      const currName = curr ? this.resourceNameString(curr) : null
      if (prevName !== currName) this.headerChipExpanded = false
    }
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('mouseover', this.onTruncatableMouseOver)
  }

  private onTruncatableMouseOver = (event: Event): void => {
    const target = event.target as HTMLElement
    if (!target.classList?.contains('inline-copy-text')) return
    target.classList.toggle('is-truncated', target.scrollWidth > target.clientWidth)
  }

  toggleHeaderChipExpand(event: Event): void {
    event.stopPropagation()
    this.headerChipExpanded = !this.headerChipExpanded
  }

  /**
   * Assign a host element ID from the first issue so scrollToIssue() can locate
   * this component via document.getElementById('issue-${issueId}').
   */
  @HostBinding('attr.id') get issueElementId(): string {
    const firstId = this.subGroup?.issues?.[0]?.issueId
    return firstId ? `issue-${firstId}` : null
  }

  // Last 8 chars of the issueId for compact display next to the timestamp; the
  // full ID is what actually gets copied when the chip is clicked.
  shortIssueId(issueId: string): string {
    return issueId ? issueId.slice(-8) : ''
  }

  // Thin wrapper so the template can call this directly. The formatter itself
  // lives in DateFormattingService — reusable across other components that want
  // the same "May 18 at 11:43 AM" local-time display.
  shortTimestamp(timestamp: Date): string {
    return this.dateFormat.getLocalDateAtTime(timestamp)
  }

  // Click-to-copy — copies the bare issueId and flips the hover-revealed icon
  // to a checkmark via CopyFeedbackService. Per-issue key prevents two visible
  // chips from sharing state.
  copyIssueId(issue: ViewableIssue, event: Event): void {
    if (!issue?.issueId) return
    event.stopPropagation()
    this.clipboard.copy(issue.issueId).then((ok) => {
      if (ok) this.copyFeedback.onCopied(`issue-id-${issue.issueId}`, this.cdr)
    })
  }

  // Tracks which issue's error body is shown. Stored as issueId (not object reference)
  // so the selection survives subGroup updates from polling.
  private _selectedIssueId: string = null

  get activeIssue(): ViewableIssue {
    if (!this.subGroup) return null
    if (this._selectedIssueId) {
      const found = this.subGroup.issues.find((i) => i.issueId === this._selectedIssueId)
      if (found) return found
    }
    return this.subGroup.issues[0]
  }

  // Selectable when multiple issues have different resource names — each issue's body
  // will render different resource-specific content. Exact-match groups (all same name)
  // don't need selection since the bodies are identical.
  get hasSelectableErrors(): boolean {
    if (!this.subGroup || this.subGroup.issues.length <= 1) return false
    const firstName = this.resourceNameString(this.subGroup.issues[0])
    return this.subGroup.issues.some((i) => this.resourceNameString(i) !== firstName)
  }

  selectIssue(issue: ViewableIssue): void {
    if (!this.hasSelectableErrors) return
    this._selectedIssueId = issue.issueId
  }

  isSelectedIssue(issue: ViewableIssue): boolean {
    if (!this.hasSelectableErrors) return false // exact-match groups: none selected
    if (!this._selectedIssueId) return issue === this.subGroup?.issues[0]
    return issue.issueId === this._selectedIssueId
  }

  /**
   * Issues beyond the first — used for anchor elements so scrollToIssue() can
   * find any issue in a multi-issue content group by its ID.
   */
  get secondaryIssues(): ViewableIssue[] {
    return this.subGroup?.issues?.slice(1) ?? []
  }

  /** Distinct resource type(s) for the header-end area. Empty when grouped: accordionLabel
   *  already renders the same list as the title, and showing it twice doubled the width
   *  the header demanded. */
  private computeResourceTypeLabel(): string {
    if (!this.subGroup || this.grouped) return ''
    return this.distinctResourceTypes().join(', ')
  }

  // When grouped, show resource type names; when standalone, show the issue title/display string
  private computeAccordionLabel(): string {
    if (this.grouped && this.subGroup) {
      const types = this.distinctResourceTypes()
      if (types.length > 0) return types.join(', ')
    }
    return (
      this.subGroup?.representative?.issue?.title ?? this.subGroup?.representative?.issue?.issueTypeDisplayString ?? ''
    )
  }

  // Sorted to match the parent group header's label, which lists the same types.
  private distinctResourceTypes(): string[] {
    const types = new Set<string>()
    for (const issue of this.subGroup?.issues ?? []) {
      const t = this.resourceTypeString(issue)
      if (t) types.add(t)
    }
    return Array.from(types).sort()
  }

  get panelDisplayed(): boolean {
    if (this.flat) return true // flat mode is always "expanded"
    return this.subGroup?.expanded ?? false
  }

  togglePanel() {
    if (this.subGroup) {
      this.subGroup.expanded = !this.subGroup.expanded
    }
  }

  expand() {
    if (this.subGroup) {
      this.subGroup.expanded = true
    }
  }

  collapse() {
    if (this.subGroup) {
      this.subGroup.expanded = false
    }
  }

  resourceTypeString(issue: ViewableIssue): string {
    if (issue.resourceType) {
      return getResourceTypeDisplayString(issue.resourceType, issue.resourceVariant)
    }
    return ''
  }

  // Fall back to extracting name from ARN when the API didn't provide a display name
  resourceNameString(issue: ViewableIssue): string {
    if (!issue.resourceName && issue.arn) {
      return getResourceNameFromArn(issue.arn)
    }
    return issue.resourceName
  }

  getConsoleUrl(issue: ViewableIssue): string {
    return consoleUrl(issue.arn)
  }

  get hasAnyResourceName(): boolean {
    if (!this.subGroup) return false
    return this.subGroup.issues.some((i) => !!this.resourceNameString(i))
  }

  /**
   * Show the affected resources list in the panel when:
   * - Multiple issues (resources listed as chips/list)
   * - Single issue in flat mode (no accordion header to show it in)
   * Single issues in accordion mode show the resource in the header instead.
   */
  get showResourceList(): boolean {
    if (!this.hasAnyResourceName) return false
    if (this.subGroup?.issues.length === 1 && !this.flat) return false
    return true
  }

  onResourceArnCopied(index: number) {
    this.copyFeedback.onCopied(`arn-${index}`, this.cdr)
  }

  /**
   * Group issues by resource type, compute diff-highlight segments per group.
   * Within each group, finds the common prefix/suffix so the unique middle
   * part of each resource name can be visually highlighted.
   */
  get resourceTypeGroups(): ResourceTypeGroup[] {
    if (!this.subGroup) return []
    const map = new Map<string, ViewableIssue[]>()
    const order: string[] = []
    for (const issue of this.subGroup.issues) {
      const label = this.resourceTypeString(issue) || ''
      if (!map.has(label)) {
        map.set(label, [])
        order.push(label)
      }
      map.get(label).push(issue)
    }

    return order.map((label) => {
      const issues = map.get(label)
      const names = issues.map((i) => this.resourceNameString(i) || '')
      const prefix = names.length > 1 ? commonPrefix(names) : ''
      let suffix = names.length > 1 ? commonSuffix(names) : ''

      // commonPrefix and commonSuffix can overlap when names are identical or highly
      // repetitive (each can return most or all of the string). Clamp the suffix so the
      // two slices never cross the shortest name; otherwise the unique-middle substring
      // below inverts and the name renders multiple times.
      const minNameLen = Math.min(...names.map((n) => n.length))
      if (prefix.length + suffix.length > minNameLen) {
        suffix = suffix.slice(prefix.length + suffix.length - minNameLen)
      }

      // Only condense when the shared prefix+suffix is the majority of the name and at
      // least one name still has a unique middle to highlight (identical names have none).
      // Prevents misleading condensing when names share a short prefix but are
      // fundamentally different (e.g. different deployments in the same cluster).
      const sharedLen = prefix.length + suffix.length
      const avgNameLen = names.reduce((sum, n) => sum + n.length, 0) / names.length
      const anyUnique = names.some((n) => n.length > sharedLen)
      const hasDiff = names.length > 1 && sharedLen > 0 && anyUnique && sharedLen / avgNameLen > 0.6
      const entries: ResourceEntry[] = issues.map((issue) => {
        const full = this.resourceNameString(issue) || ''
        const unique = full.substring(prefix.length, full.length - suffix.length)
        return {
          issue,
          segments: { prefix, unique, suffix, full, hasDiff },
          consoleUrl: this.getConsoleUrl(issue),
          typeLabel: this.resourceTypeString(issue),
        }
      })

      return { label, entries }
    })
  }

  trackByIssueId(index: number, entry: ResourceEntry) {
    return entry.issue.issueId
  }

  trackByResourceType(index: number, group: ResourceTypeGroup) {
    return group.label
  }
}
